import type {
  CreateParams,
  CreateResult,
  DataProvider,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  Identifier,
  RaRecord,
  UpdateParams,
  UpdateResult,
} from 'react-admin';

import { apiFetch } from './api/client';

interface PaginationMeta {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

interface PaginatedResponse<T> {
  data: T[];
  meta: PaginationMeta;
}

interface ListEnvelope<T> {
  data: T[];
}

const RESOURCE_PATHS: Record<string, string> = {
  businesses: '/admin/businesses',
  templates: '/admin/templates',
  leads: '/admin/leads',
  feedback: '/admin/feedback',
  users: '/admin/users',
  categories: '/admin/geo/categories',
  countries: '/admin/geo/countries',
  states: '/admin/geo/states',
  cities: '/admin/geo/cities',
};

const ENVELOPE_LIST_RESOURCES = new Set([
  'templates',
  'categories',
  'countries',
  'states',
  'cities',
]);

function getResourcePath(resource: string): string {
  const path = RESOURCE_PATHS[resource];
  if (!path) {
    throw new Error(`Unknown resource: ${resource}`);
  }
  return path;
}

function buildQuery(params: GetListParams): string {
  const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 };
  const { field, order } = params.sort ?? { field: 'id', order: 'ASC' };
  const searchParams = new URLSearchParams();

  searchParams.set('page', String(page));
  searchParams.set('page_size', String(perPage));

  if (field && order) {
    searchParams.set('sort', field);
    searchParams.set('order', order.toLowerCase());
  }

  Object.entries(params.filter ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    searchParams.set(key, String(value));
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

async function getListFromPaginated<T extends RaRecord>(
  resource: string,
  params: GetListParams,
): Promise<GetListResult<T>> {
  const path = getResourcePath(resource);
  const query = buildQuery(params);
  const response = await apiFetch<PaginatedResponse<T>>(`${path}${query}`);

  return {
    data: response.data.map((record) => ({
      ...record,
      id: record.id as Identifier,
    })),
    total: response.meta.total,
  };
}

async function getListFromEnvelope<T extends RaRecord>(
  resource: string,
  params?: GetListParams,
): Promise<GetListResult<T>> {
  const path = getResourcePath(resource);
  const query = params ? buildQuery(params) : '';
  const response = await apiFetch<ListEnvelope<T>>(`${path}${query}`);

  return {
    data: response.data.map((record) => ({
      ...record,
      id: record.id as Identifier,
    })),
    total: response.data.length,
  };
}

export const dataProvider: DataProvider = {
  getList: async (resource, params) => {
    if (ENVELOPE_LIST_RESOURCES.has(resource)) {
      return getListFromEnvelope(resource, params);
    }
    return getListFromPaginated(resource, params);
  },

  getOne: async <RecordType extends RaRecord = RaRecord>(
    resource: string,
    params: GetOneParams<RecordType>,
  ): Promise<GetOneResult<RecordType>> => {
    const path = getResourcePath(resource);

    try {
      const record = await apiFetch<RecordType>(`${path}/${params.id}`);
      return {
        data: { ...record, id: (record.id ?? params.id) as Identifier },
      };
    } catch (error) {
      if (resource === 'templates') {
        const list = await getListFromEnvelope<RecordType>(resource, undefined);
        const record = list.data.find((item) => String(item.id) === String(params.id));
        if (record) {
          return { data: record };
        }
      }
      throw error;
    }
  },

  getMany: async (resource, params: GetManyParams) => {
    const results = await Promise.all(
      params.ids.map(async (id) => {
        const { data } = await dataProvider.getOne(resource, { id });
        return data;
      }),
    );
    return { data: results } as GetManyResult;
  },

  getManyReference: async (resource, params) => {
    return dataProvider.getList(resource, {
      ...params,
      filter: {
        ...params.filter,
        [params.target]: params.id,
      },
    });
  },

  create: async (resource, params: CreateParams) => {
    const path = getResourcePath(resource);
    const record = await apiFetch<RaRecord>(path, {
      method: 'POST',
      body: params.data,
    });

    return {
      data: {
        ...params.data,
        ...record,
        id: (record.id ?? params.data.id) as Identifier,
      },
    } as CreateResult;
  },

  update: async (resource, params: UpdateParams) => {
    const path = getResourcePath(resource);
    const record = await apiFetch<RaRecord>(`${path}/${params.id}`, {
      method: 'PATCH',
      body: params.data,
    });

    return {
      data: {
        ...params.previousData,
        ...params.data,
        ...record,
        id: params.id,
      },
    } as UpdateResult;
  },

  updateMany: async (resource, params) => {
    const results = await Promise.all(
      params.ids.map((id) =>
        dataProvider.update(resource, {
          id,
          data: params.data,
          previousData: { id } as RaRecord,
        }),
      ),
    );
    return { data: results.map(({ data }) => data.id) };
  },

  delete: async (resource, params: DeleteParams) => {
    const path = getResourcePath(resource);
    await apiFetch(`${path}/${params.id}`, { method: 'DELETE' });
    return { data: params.previousData ?? ({ id: params.id } as RaRecord) } as DeleteResult;
  },

  deleteMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        apiFetch(`${getResourcePath(resource)}/${id}`, { method: 'DELETE' }),
      ),
    );
    return { data: params.ids };
  },
};
